import * as PIXI from 'pixi.js'
import { useEffect, useRef } from 'react'
import { ZoomControls } from '../components/ZoomControls'
import { getMobileDPRCap, useIsMobile } from '../lib/useMobile'
import { heliocentricPositionAU, dateToJD } from '../lib/ephemeris'
import type { ObjectData } from '../lib/types'

type PlanetElements = {
  a: number
  e: number
  i: number
  omega: number
  w: number
  M: number
  epoch_jd: number
  q?: number | null
}

type SceneProps = {
  objectData: ObjectData | null
  planets: Record<string, PlanetElements> | null
  timeMs: number
  focusPlanet?: string | null
  orientation?: 'nasa' | 'mirrored'
  onBackgroundClick?: () => void
}

const COLORS = {
  background: '#0b0b0b',
  sun: 0xffcc33,
  orbit: 0x3a3a3a,
  iso: 0xff7a1a,
  mercury: 0xb7b7b7,
  venus: 0xf4c26b,
  earth: 0x9fb6ff,
  mars: 0xff8a63,
  jupiter: 0xd8b98e,
  saturn: 0xe6d4a9,
  uranus: 0x9fe8ff,
  neptune: 0x83a6ff,
  pluto: 0xcccccc,
} as const

const PLANETS_META = [
  { name: 'Mercury', radius: 2.2, color: COLORS.mercury },
  { name: 'Venus', radius: 2.6, color: COLORS.venus },
  { name: 'Earth', radius: 3.0, color: COLORS.earth },
  { name: 'Mars', radius: 2.6, color: COLORS.mars },
  { name: 'Jupiter', radius: 3.6, color: COLORS.jupiter },
  { name: 'Saturn', radius: 3.4, color: COLORS.saturn },
  { name: 'Uranus', radius: 2.8, color: COLORS.uranus },
  { name: 'Neptune', radius: 2.8, color: COLORS.neptune },
  { name: 'Pluto', radius: 2.2, color: COLORS.pluto },
] as const

const PLANET_FALLBACK: Record<string, PlanetElements> = {
  Mercury: { a: 0.38709927, e: 0.20563593, i: 7.00497902, omega: 48.33076593, w: 29.124279, M: 174.795884, epoch_jd: 2459396.5, q: 0.38709927 * (1 - 0.20563593) },
  Venus: { a: 0.72333566, e: 0.00677672, i: 3.39467605, omega: 76.67984255, w: 54.92126, M: 50.376632, epoch_jd: 2459396.5, q: 0.72333566 * (1 - 0.00677672) },
  Earth: { a: 1.00000261, e: 0.01671123, i: -0.00001531, omega: -11.26064, w: 102.94719, M: 100.46435, epoch_jd: 2459396.5, q: 1.00000261 * (1 - 0.01671123) },
  Mars: { a: 1.52371034, e: 0.0933941, i: 1.84969142, omega: 49.55953891, w: 286.537, M: 355.45332, epoch_jd: 2459396.5, q: 1.52371034 * (1 - 0.0933941) },
  Jupiter: { a: 5.202887, e: 0.04838624, i: 1.30439695, omega: 100.473909, w: 273.867, M: 19.65053, epoch_jd: 2459396.5, q: 5.202887 * (1 - 0.04838624) },
  Saturn: { a: 9.53667594, e: 0.05386179, i: 2.48599187, omega: 113.662424, w: 339.392, M: 317.0207, epoch_jd: 2459396.5, q: 9.53667594 * (1 - 0.05386179) },
  Uranus: { a: 19.18916464, e: 0.04725744, i: 0.77263783, omega: 74.016925, w: 96.998857, M: 142.2386, epoch_jd: 2459396.5, q: 19.18916464 * (1 - 0.04725744) },
  Neptune: { a: 30.06992276, e: 0.00859048, i: 1.77004347, omega: 131.784225, w: 272.8461, M: 260.2471, epoch_jd: 2459396.5, q: 30.06992276 * (1 - 0.00859048) },
  Pluto: { a: 39.48211675, e: 0.2488273, i: 17.14001206, omega: 110.303936, w: 224.06676, M: 14.86014, epoch_jd: 2459396.5, q: 39.48211675 * (1 - 0.2488273) },
}

const FIT_RADIUS_AU = 35
const ISO_SPAN_DAYS = 360

type PointerState = {
  x: number
  y: number
  startX: number
  startY: number
  moved: boolean
}

const keplerPeriodDays = (a: number) => Math.max(1, Math.pow(Math.max(a, 0.1), 1.5) * 365.25)

const clampScale = (value: number, base: number) => Math.max(base * 0.3, Math.min(base * 12, value))

const getBaseScale = (renderer: PIXI.Renderer) =>
  (Math.min(renderer.width, renderer.height) * 0.46) / FIT_RADIUS_AU

export default function Scene({
  objectData,
  planets,
  timeMs,
  focusPlanet = null,
  orientation = 'nasa',
  onBackgroundClick,
}: SceneProps) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const appRef = useRef<PIXI.Application | null>(null)
  const gRef = useRef<PIXI.Graphics | null>(null)
  const camRef = useRef({ scaleAU: 1, offsetX: 0, offsetY: 0 })

  const isMobile = useIsMobile()
  const isMobileRef = useRef(isMobile)

  const timeRef = useRef(timeMs)
  const objectRef = useRef(objectData)
  const planetsRef = useRef(planets)
  const focusRef = useRef(focusPlanet)
  const orientationRef = useRef<'nasa' | 'mirrored'>(orientation)
  const backgroundClickRef = useRef(onBackgroundClick)

  timeRef.current = timeMs
  objectRef.current = objectData
  planetsRef.current = planets
  focusRef.current = focusPlanet
  orientationRef.current = orientation
  backgroundClickRef.current = onBackgroundClick

  if (typeof window !== 'undefined' && (window as any).__ASTRO_PIXI_APP && !appRef.current) {
    appRef.current = (window as any).__ASTRO_PIXI_APP as PIXI.Application
  }

  const fitCamera = () => {
    const app = appRef.current
    if (!app) return
    const renderer = app.renderer
    if (!renderer) return
    const base = getBaseScale(renderer)
    camRef.current.scaleAU = base
    camRef.current.offsetX = 0
    camRef.current.offsetY = 0
  }

  const zoomCamera = (factor: number) => {
    const app = appRef.current
    if (!app) return
    const renderer = app.renderer
    if (!renderer) return
    const base = getBaseScale(renderer)
    camRef.current.scaleAU = clampScale(camRef.current.scaleAU * factor, base)
  }

  useEffect(() => {
    isMobileRef.current = isMobile
    const app = appRef.current
    if (!app) return
    const renderer = app.renderer
    if (!renderer) return
    const dpr = window.devicePixelRatio || 1
    const desired = isMobile ? Math.min(dpr, getMobileDPRCap()) : dpr
    if (renderer.resolution !== desired) {
      renderer.resolution = desired
      const host = hostRef.current
      if (host) {
        renderer.resize(host.clientWidth, host.clientHeight)
      }
    }
  }, [isMobile])

  useEffect(() => {
    if (appRef.current) {
      const host = hostRef.current
      const app = appRef.current
      if (host && app && !host.contains(app.canvas)) {
        host.appendChild(app.canvas)
      }
      return
    }

    const host = hostRef.current
    if (!host) return

    const app = new PIXI.Application()
    appRef.current = app
    ;(window as any).__ASTRO_PIXI_APP = app

    const pointers = new Map<number, PointerState>()
    let lastTap = 0
    let startOffset = { x: 0, y: 0 }
    let startDist = 0
    let startScale = 1
    const dist = (a: PointerState, b: PointerState) => Math.hypot(a.x - b.x, a.y - b.y)

    let resizeObserver: ResizeObserver | null = null
    let disposed = false

    ;(async () => {
      try {
        const dpr = window.devicePixelRatio || 1
        const initialResolution = isMobileRef.current ? Math.min(dpr, getMobileDPRCap()) : dpr
        await app.init({
          background: COLORS.background,
          resizeTo: host,
          antialias: true,
          powerPreference: 'high-performance',
          resolution: initialResolution,
        })
        if (disposed) {
          app.destroy(true)
          return
        }

        host.appendChild(app.canvas)

        const graphics = new PIXI.Graphics()
        gRef.current = graphics
        app.stage.addChild(graphics)

        fitCamera()

        resizeObserver = new ResizeObserver(() => fitCamera())
        resizeObserver.observe(host)

        app.canvas.style.touchAction = 'none'

        const onWheel = (event: WheelEvent) => {
          event.preventDefault()
          const renderer = app.renderer
          if (!renderer) return
          const base = getBaseScale(renderer)
          const factor = event.deltaY < 0 ? 1.1 : 0.9
          camRef.current.scaleAU = clampScale(camRef.current.scaleAU * factor, base)
        }

        const onPointerDown = (event: PointerEvent) => {
          const renderer = app.renderer
          if (!renderer) return

          const isFirstPointer = pointers.size === 0
          const entry: PointerState = {
            x: event.clientX,
            y: event.clientY,
            startX: event.clientX,
            startY: event.clientY,
            moved: false,
          }

          pointers.set(event.pointerId, entry)
          ;(event.target as HTMLElement).setPointerCapture?.(event.pointerId)

          const now = performance.now()
          if (isFirstPointer && now - lastTap < 300) {
            entry.moved = true
            fitCamera()
            startOffset = { x: camRef.current.offsetX, y: camRef.current.offsetY }
            startScale = camRef.current.scaleAU
            startDist = 0
          }
          lastTap = now

          if (pointers.size === 1) {
            startOffset = { x: camRef.current.offsetX, y: camRef.current.offsetY }
            startScale = camRef.current.scaleAU
            startDist = 0
          } else if (pointers.size === 2) {
            const values = [...pointers.values()]
            startDist = dist(values[0], values[1])
            startScale = camRef.current.scaleAU
            values[0].moved = true
            values[1].moved = true
          }
        }

        const onPointerMove = (event: PointerEvent) => {
          const entry = pointers.get(event.pointerId)
          if (!entry) return

          entry.x = event.clientX
          entry.y = event.clientY

          if (pointers.size === 1) {
            const dx = entry.x - entry.startX
            const dy = entry.y - entry.startY
            if (!entry.moved && Math.hypot(dx, dy) > 6) entry.moved = true
            camRef.current.offsetX = startOffset.x + dx
            camRef.current.offsetY = startOffset.y + dy
          } else if (pointers.size === 2) {
            const values = [...pointers.values()]
            const distance = dist(values[0], values[1])
            if (startDist > 0) {
              const renderer = app.renderer
              if (renderer) {
                const base = getBaseScale(renderer)
                const next = Math.max(startScale * (distance / startDist), 0.0001)
                camRef.current.scaleAU = clampScale(next, base)
              }
            }
          }
        }

        const onPointerEnd = (event: PointerEvent) => {
          const entry = pointers.get(event.pointerId)
          if (!entry) return

          pointers.delete(event.pointerId)
          if (app.canvas.hasPointerCapture?.(event.pointerId)) {
            app.canvas.releasePointerCapture(event.pointerId)
          }

          if (!entry.moved && pointers.size === 0) {
            backgroundClickRef.current?.()
          }

          if (pointers.size === 1) {
            const remaining = [...pointers.values()][0]
            remaining.startX = remaining.x
            remaining.startY = remaining.y
            remaining.moved = false
            startOffset = { x: camRef.current.offsetX, y: camRef.current.offsetY }
            startScale = camRef.current.scaleAU
            startDist = 0
          } else {
            startDist = 0
          }
        }

        app.canvas.addEventListener('wheel', onWheel, { passive: false })
        app.canvas.addEventListener('pointerdown', onPointerDown)
        app.canvas.addEventListener('pointermove', onPointerMove)
        window.addEventListener('pointerup', onPointerEnd)
        window.addEventListener('pointercancel', onPointerEnd)

        const draw = () => {
          const graphics = gRef.current
          if (!graphics) return

          const renderer = app.renderer
          if (!renderer) return

          const width = renderer.width
          const height = renderer.height
          const { scaleAU, offsetX, offsetY } = camRef.current
          const cx = width / 2 + offsetX
          const cy = height / 2 + offsetY
          const jd = dateToJD(timeRef.current)

          const mapY = (value: number) =>
            orientationRef.current === 'nasa' ? cy + value * scaleAU : cy - value * scaleAU

          graphics.clear()
          graphics.circle(cx, cy, 7).fill({ color: COLORS.sun })
          graphics.stroke({ width: 1, color: 0x262626, alpha: 0.45 }).circle(cx, cy, 5.2 * scaleAU)
          graphics.stroke({ width: 1, color: 0x1f1f1f, alpha: 0.3 }).circle(cx, cy, 30 * scaleAU)

          const focusUpper = focusRef.current ? focusRef.current.toUpperCase() : null

          for (const planet of PLANETS_META) {
            const elements = planetsRef.current?.[planet.name] ?? PLANET_FALLBACK[planet.name]
            if (!elements) continue

            const periodDays = keplerPeriodDays(elements.a)
            const samples = 240
            for (let i = 0; i < samples; i++) {
              if (i % 12 >= 7) continue
              const t0 = i / samples
              const t1 = (i + 1) / samples
              const pos0 = heliocentricPositionAU(elements, elements.epoch_jd + t0 * periodDays)
              const pos1 = heliocentricPositionAU(elements, elements.epoch_jd + t1 * periodDays)
              graphics
                .moveTo(cx + pos0.x * scaleAU, mapY(pos0.y))
                .lineTo(cx + pos1.x * scaleAU, mapY(pos1.y))
                .stroke({ width: 0.8, color: COLORS.orbit, alpha: 0.45 })
            }

            const current = heliocentricPositionAU(elements, jd)
            const px = cx + current.x * scaleAU
            const py = mapY(current.y)
            graphics.circle(px, py, planet.radius).fill({ color: planet.color, alpha: 0.95 })
            if (focusUpper && focusUpper.startsWith(planet.name.toUpperCase())) {
              graphics.circle(px, py, planet.radius + 4).stroke({ width: 1.2, color: 0xffffff, alpha: 0.5 })
            }
          }

          if (focusUpper === 'SUN') {
            graphics.circle(cx, cy, 12).stroke({ width: 1.2, color: 0xffffff, alpha: 0.3 })
          }

          const object = objectRef.current
          if (object?.elements) {
            const segmentCount = 260
            for (let i = 0; i < segmentCount; i++) {
              if (i % 8 >= 6) continue
              const dt0 = (i / segmentCount) * ISO_SPAN_DAYS - ISO_SPAN_DAYS / 2
              const dt1 = ((i + 1) / segmentCount) * ISO_SPAN_DAYS - ISO_SPAN_DAYS / 2
              const p0 = heliocentricPositionAU(object.elements, jd + dt0)
              const p1 = heliocentricPositionAU(object.elements, jd + dt1)
              graphics
                .moveTo(cx + p0.x * scaleAU, mapY(p0.y))
                .lineTo(cx + p1.x * scaleAU, mapY(p1.y))
                .stroke({ width: 1.6, color: COLORS.iso, alpha: 0.9 })
            }

            const current = heliocentricPositionAU(object.elements, jd)
            const px = cx + current.x * scaleAU
            const py = mapY(current.y)
            graphics.circle(px, py, 5).stroke({ width: 1.2, color: COLORS.iso, alpha: 0.7 })
            graphics.circle(px, py, 2.2).fill({ color: COLORS.iso })
          }
        }

        app.ticker.add(draw)

        const cleanup = () => {
          pointers.clear()
          app.ticker.remove(draw)
          app.canvas.removeEventListener('wheel', onWheel)
          app.canvas.removeEventListener('pointerdown', onPointerDown)
          app.canvas.removeEventListener('pointermove', onPointerMove)
          window.removeEventListener('pointerup', onPointerEnd)
          window.removeEventListener('pointercancel', onPointerEnd)
          resizeObserver?.disconnect()
        }

        (app as any).__astroCleanup = cleanup
      } catch (error) {
        console.error('Pixi init failed', error)
      }
    })()

    return () => {
      disposed = true
      const appInstance = appRef.current
      if (appInstance) {
        try {
          if ((appInstance as any).__astroCleanup) {
            ;(appInstance as any).__astroCleanup()
          }
          appInstance.destroy(true)
        } catch (error) {
          console.warn('Pixi cleanup failed', error)
        }
      }
      appRef.current = null
      gRef.current = null
      delete (window as any).__ASTRO_PIXI_APP
    }
  }, [])

  return (
    <div className="relative h-full w-full bg-[#050505]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(255,255,255,0.08),rgba(0,0,0,0)_65%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(39,89,255,0.12),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,128,0,0.08),transparent_60%)]" />
      <div ref={hostRef} className="absolute inset-0 [touch-action:none]" />
      <ZoomControls onZoomIn={() => zoomCamera(1.2)} onZoomOut={() => zoomCamera(1 / 1.2)} onReset={fitCamera} />
    </div>
  )
}
